import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useQuery } from '@tanstack/react-query';
import { getCustomers } from '@/services/customerService';
import { getServices } from '@/services/serviceService';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { Subscription } from '@/lib/data';
import CustomerSection from './CustomerSection';
import InstallationSection from './InstallationSection';
import ServicesSection from './ServicesSection';
import ContactSection from './ContactSection';
import IntegrationSection from './IntegrationSection';
import { useServiceSelection } from '@/hooks/useServiceSelection';
import { 
  AllowedStatus, 
  createSubscriptionFormSchema, 
  SubscriptionFormValues 
} from './SubscriptionFormTypes';
import { 
  createDefaultValues,
  useSubscriptionFormHelpers
} from './SubscriptionFormUtils';
import { useLanguage } from '@/hooks/useLanguage';

interface SubscriptionFormProps {
  subscription?: Subscription;
  isLoading?: boolean;
  onSubmit: (data: any) => void;
  allowedStatuses?: AllowedStatus[];
  disableNewCustomer?: boolean;
  hideServices?: boolean;
}

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  subscription,
  isLoading = false,
  onSubmit,
  allowedStatuses = ["registered", "active", "terminated", "cancelled"],
  disableNewCustomer = false,
  hideServices = false
}) => {
  const { translate } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialCustomerId = searchParams.get('customer') || subscription?.customer_id || '';
  const { user } = useAuth();
  
  const showServices = !hideServices;
  const subscriptionFormSchema = createSubscriptionFormSchema(allowedStatuses, showServices);

  const { data: customers = [], isLoading: isLoadingCustomers, refetch: refetchCustomers } = useQuery({
    queryKey: ['customers'],
    queryFn: getCustomers // Fixed: Removed the argument that was being passed
  });
  
  const { data: availableServices = [] } = useQuery({
    queryKey: ['services'],
    queryFn: getServices,
    enabled: showServices
  });
  
  const isEditMode = !!subscription;

  const defaultStatus = allowedStatuses.includes('registered') 
    ? 'registered' 
    : allowedStatuses[0];
    
  // Create default values for the form
  const defaultValues = useCallback(() => {
    return createDefaultValues(
      initialCustomerId, 
      subscription,
      availableServices, 
      showServices, 
      defaultStatus
    );
  }, [initialCustomerId, subscription, availableServices, showServices, defaultStatus]);
  
  // Type-safe form setup
  const form = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionFormSchema),
    defaultValues: defaultValues() as any // Type assertion here is necessary due to conditional typing
  });

  const [selectedServices, setSelectedServices] = useState<Record<string, boolean>>({});
  const [priceOverrides, setPriceOverrides] = useState<Record<string, number>>({});

  // Fix infinite loop by setting state in useEffect
  useEffect(() => {
    if (isEditMode && subscription?.services) {
      const selected: Record<string, boolean> = {};
      const overrides: Record<string, number> = {};

      subscription.services.forEach(service => {
        selected[service.service_id] = true;
        overrides[service.service_id] = service.price;
      });

      setSelectedServices(selected);
      setPriceOverrides(overrides);
    }
  }, [isEditMode, subscription]);
  
  const { 
    selectedServices: selectedServicesData,
    priceOverrides: priceOverridesData,
    handleServiceSelection,
    canSelectService,
    handlePriceChange
  } = useServiceSelection(availableServices, selectedServices);

  // Get form helper functions
  const { 
    handleServiceSelectionChange: baseHandleServiceSelectionChange, 
    handleServicePriceChange: baseHandleServicePriceChange 
  } = useSubscriptionFormHelpers(form, showServices);

  // Update form with services data when availableServices changes
  useEffect(() => {
    if (showServices && availableServices.length > 0 && form) {
      const servicesFormData = availableServices.map(service => {
        const existingService = subscription?.services?.find(s => s.service_id === service.service_id);
        
        return {
          service_id: service.service_id,
          price: existingService?.price || service.default_price,
          selected: !!existingService
        };
      });
      
      if (showServices) {
        form.setValue('services', servicesFormData as any);
      }
    }
  }, [availableServices, subscription, form, showServices]);

  useEffect(() => {
    if (initialCustomerId) {
      form.setValue('customer_id', initialCustomerId);
    }
  }, [initialCustomerId, form]);
  
  // Handle service selection changes
  const handleServiceSelectionChange = useCallback((service: any, selected: boolean) => {
    baseHandleServiceSelectionChange(service, selected, handleServiceSelection);
  }, [baseHandleServiceSelectionChange, handleServiceSelection]);
  
  // Handle price changes for services
  const handleServicePriceChange = useCallback((serviceId: string, value: string) => {
    baseHandleServicePriceChange(serviceId, value, handlePriceChange);
  }, [baseHandleServicePriceChange, handlePriceChange]);

  // Handle form submission
  const handleFormSubmit = async (data: SubscriptionFormValues) => {
    const submissionData = { ...data };
    
    if (showServices) {
      // This is safe because we know if showServices is true, we have services in the form
      const formWithServices = submissionData as any;
      if (formWithServices.services) {
        // Filter only selected services
        formWithServices.services = formWithServices.services.filter((s: any) => s.selected);
      }
    }
    
    if (!submissionData.customer_id) {
      toast.error(translate('subscriptions.invalidCustomer'));
      return;
    }
    
    onSubmit(submissionData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <CustomerSection
          form={form}
          customers={customers}
          isLoading={isLoadingCustomers || isLoading}
          isEditMode={isEditMode}
          disableNewCustomer={disableNewCustomer}
          refetchCustomers={refetchCustomers}
        />
        
        <InstallationSection
          form={form}
          isLoading={isLoading}
          allowedStatuses={allowedStatuses}
        />

        <ContactSection form={form} isLoading={isLoading} />
        
        <IntegrationSection form={form} isLoading={isLoading} />
        
        {showServices && (
          <ServicesSection
            availableServices={availableServices}
            selectedServices={selectedServicesData}
            priceOverrides={priceOverridesData}
            onServiceSelection={handleServiceSelectionChange}
            onPriceChange={handleServicePriceChange}
            canSelectService={canSelectService}
          />
        )}
        
        <div className="flex justify-end space-x-4">
          <Button 
            type="button" 
            variant="outline" 
            disabled={isLoading}
            onClick={() => window.history.back()}
          >
            {translate('common.cancel')}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 
              isEditMode ? translate('subscriptions.updating') : translate('subscriptions.creating') : 
              isEditMode ? translate('subscriptions.updateSubscription') : translate('subscriptions.createSubscription')}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default SubscriptionForm;
